import React, { useState, useEffect, useRef } from 'react';
import { Save, Loader2, AlertCircle, BadgeCheck, X } from 'lucide-react';
import { InstagramAccount, Plan } from '../../types';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { products } from '../../stripe-config';
import { CheckoutButton } from '../../components/Plans/CheckoutButton';
import { CancellationModal } from '../../components/Plans/CancellationModal';
import { TrialBanner } from '../../components/Plans/TrialBanner';

// Declare iClosed widget for TypeScript
declare global {
  interface Window {
    iClosed?: any;
  }
}

interface PlansProps {
  account: InstagramAccount;
  plans: Plan[];
  isDarkMode: boolean;
  language: string;
  onAccountDeleted: () => Promise<void>;
}

export function Plans({ account, plans, isDarkMode, language, onAccountDeleted }: PlansProps) {
  const navigate = useNavigate();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(() => {
    // Set annual as default if no subscription
    if (!account.subscription_status || account.subscription_status === 'not_started') {
      return true;
    }
    // Otherwise use the account's subscription period
    return account.subscription_period === 'annual';
  });
  const [localSubscriptionEndDate, setLocalSubscriptionEndDate] = useState(account.subscription_end_date);
  const t = translations[language as keyof typeof translations];

  // Initialize iClosed calendar for NEW legacy clients
  useEffect(() => {
    if (account.is_legacy_client === 'NEW') {
      // Clear any existing iClosed widgets
      const existingWidgets = document.querySelectorAll('.iclosed-widget iframe');
      existingWidgets.forEach(widget => widget.remove());
      
      // Reset iClosed if it exists
      if (window.iClosed) {
        window.iClosed = undefined;
      }
      
      // Load iClosed widget script
      const timer = setTimeout(() => {
        // Check if script is already loaded
        const existingScript = document.querySelector('script[src="https://app.iclosed.io/assets/widget.js"]');
        if (existingScript) {
          // Remove existing script to force reload
          existingScript.remove();
        }
        
        // Always load a fresh script
        const script = document.createElement('script');
        script.src = 'https://app.iclosed.io/assets/widget.js';
        script.async = true;
        script.onload = () => {
          // Force widget initialization after script loads
          setTimeout(() => {
            if (window.iClosed && window.iClosed.init) {
              window.iClosed.init();
            }
          }, 100);
        };
        document.head.appendChild(script);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [account.is_legacy_client, account.id]); // Add account.id as dependency

  const handleCancelSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate subscription ID
      if (!account.subscription_id) {
        setError(t.subscription_id_required || 'Subscription ID is required');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription_id: account.subscription_id
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Update local state
      setLocalSubscriptionEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString());
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err instanceof Error ? err.message : t.checkout_error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUndoCancel = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate subscription ID
      if (!account.subscription_id) {
        setError(t.subscription_id_required || 'Subscription ID is required');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription_id: account.subscription_id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }

      // Update local state
      setLocalSubscriptionEndDate(null);
    } catch (err) {
      console.error('Error undoing subscription cancellation:', err);
      setError(t.checkout_error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show cancel message if subscription is active and has an end date
  const showCancelMessage = account.subscription_status === 'active' && localSubscriptionEndDate !== null;
  const endDate = localSubscriptionEndDate 
    ? new Date(localSubscriptionEndDate).toLocaleDateString()
    : null;

  // Show cancel button if subscription is active and doesn't have an end date
  const showCancelButton = account.subscription_status === 'active' && !localSubscriptionEndDate;

  // Show trial banner if no active subscription
  const showTrialBanner = (!account.subscription_status || account.subscription_status !== 'active') && account.trial !== 'YES';

  // Show legacy client message if applicable
  if (account.is_legacy_client === 'YES') {
    return (
      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <p className={`text-sm leading-relaxed ${
          isDarkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          Vous bénéficiez actuellement d'une offre personnalisée et exclusive.
          <br />
          Pour toute demande concernant votre abonnement, vous pouvez directement contacter votre Account Manager.
          <br /><br />
          Vous avez également la possibilité de nous écrire via le chat en bas à droite du site : notre équipe prendra en charge votre demande sans aucune démarche supplémentaire de votre part.
          <br /><br />
          Merci pour votre confiance, nous restons disponibles pour vous accompagner.
        </p>
      </div>
    );
  }

  // Show only calendar for NEW legacy clients
  if (account.is_legacy_client === 'NEW') {
    return (
      <div className="flex justify-start">
        <div
          className="iclosed-widget" 
          data-url="https://app.iclosed.io/e/amplrai/plans" 
          title="Appel Stratégique Instagram" 
          style={{ width: '100%', height: '620px', textAlign: 'left' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-6 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
          {error}
        </div>
      )}

      {showTrialBanner && (
        <TrialBanner isDarkMode={isDarkMode} language={language} />
      )}

      {showCancelMessage && endDate && (
        <div className="p-6 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-amber-700">
              {t.cancel_subscription_warning} ({endDate})
            </p>
            <button
              onClick={handleUndoCancel}
              disabled={isLoading}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {t.please_wait}
                </>
              ) : (
                t.undo_cancellation
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className={`text-lg font-semibold ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>{t.plans}</h2>
        
        <div className={`inline-flex rounded-lg p-0.5 ${
          isDarkMode 
            ? 'bg-[#2b2b2b]' 
            : 'bg-gray-100 border border-gray-200'
        }`}>
          <button
            onClick={() => setIsAnnual(false)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              !isAnnual
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-white'
                  : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                : isDarkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {t.monthly}
          </button>
          <button
            onClick={() => setIsAnnual(true)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              isAnnual
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-white'
                  : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                : isDarkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {t.annual}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const product = products[plan.name as keyof typeof products];
          if (!product) return null;

          const is_active = account.plan === plan.name;
          const isCurrentPeriod = isAnnual === (account.subscription_period === 'annual');
          const savings = (plan.annualSavings?.[language as keyof typeof plan.annualSavings] || '') as string;
          const saveAnnuallyText = t.save_annually ? t.save_annually.replace('{savings}', savings) : '';

          return (
            <div
              key={plan.name}
              className={`rounded-xl border p-6 relative ${
                is_active && isCurrentPeriod
                  ? 'border-orange-500 ring-1 ring-orange-500'
                  : isDarkMode 
                    ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                    : 'bg-white border-[#e5e7eb]'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-orange-500 text-white text-[11px] font-medium px-3 py-1 rounded-full">
                    {t.popular}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h3 className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>{plan.name}</h3>
                <div className="mt-1 mb-4">
                  <span className={`text-2xl font-bold ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    {isAnnual 
                      ? plan.priceAnnual[language as keyof typeof plan.priceAnnual]
                      : plan.priceMonthly[language as keyof typeof plan.priceMonthly]
                    }
                  </span>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {t.per_month}
                  </span>
                  {isAnnual && savings && saveAnnuallyText && (
                    <div className="mt-1 text-sm text-emerald-500 font-medium">
                      {saveAnnuallyText}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {plan.features[language as keyof typeof plan.features].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {plan.included[index] ? (
                      <div className="relative flex-shrink-0">
                        <BadgeCheck 
                          size={14} 
                          className="text-[#F98F61]" 
                          strokeWidth={2.5} 
                        />
                      </div>
                    ) : (
                      <X size={14} className="text-gray-300 flex-shrink-0" strokeWidth={2.5} />
                    )}
                    <span className={`text-[13px] ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>{feature}</span>
                  </div>
                ))}
              </div>

              {(!is_active || !isCurrentPeriod) && (
                <CheckoutButton
                  planName={plan.name}
                  isAnnual={isAnnual}
                  language={language}
                  isDarkMode={isDarkMode}
                  accountId={account.id}
                  currentPlan={account.plan}
                  subscriptionId={account.subscription_id}
                  hasTrial={account.trial}
                />
              )}

              {is_active && isCurrentPeriod && (
                <div className="w-full mt-6 rounded-lg py-2 text-[13px] font-medium text-center bg-orange-50 text-orange-500">
                  {t.current_plan}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCancelButton && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowCancelConfirm(true)}
            className={`text-[13px] font-medium transition-colors ${
              isDarkMode
                ? 'text-gray-400 hover:text-gray-300'
                : 'text-gray-500 hover:text-gray-600'
            }`}
          >
            {t.cancel_subscription}
          </button>
        </div>
      )}

      {/* Cancellation Modal */}
      {showCancelConfirm && (
        <CancellationModal
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
          isLoading={isLoading}
          isDarkMode={isDarkMode}
          language={language}
          subscriptionId={account.subscription_id}
        />
      )}
    </div>
  );
}